import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BarChart3, 
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Download,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialReports = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [bsRes, plRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounts/reports/balance-sheet`),
        axios.get(`${API_URL}/api/accounts/reports/profit-loss`)
      ]);
      setBalanceSheet(bsRes.data);
      setProfitLoss(plRes.data);
    } catch (err) {
      console.error(err);
      toast.error(t('pages.financialReports.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    return value < 0 ? `(${currencySymbol} ${Math.abs(value).toLocaleString()})` : `${currencySymbol} ${value.toLocaleString()}`;
  };

  const AccountSection = ({ title, accounts, color }) => (
    <div className="space-y-2">
      <h3 className={`font-semibold text-sm uppercase tracking-wide ${color}`}>{title}</h3>
      <div className="space-y-1">
        {accounts?.map(acc => (
          <div key={acc.id} className={`flex justify-between text-sm py-1 px-2 hover:bg-muted/50 rounded ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="font-mono text-xs text-muted-foreground">{acc.code}</span>
              {acc.name}
            </span>
            <span className="font-mono">${formatCurrency(acc.balance)}</span>
          </div>
        ))}
        {(!accounts || accounts.length === 0) && (
          <div className="text-sm text-muted-foreground py-2 px-2">{t('pages.financialReports.noAccounts')}</div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.financialReports.title')}</h1>
          <p className="text-muted-foreground">{t('pages.financialReports.subtitle')}</p>
        </div>
        <Button onClick={fetchReports} variant="outline" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <RefreshCw className="h-4 w-4" /> {t('pages.financialReports.refresh')}
        </Button>
      </div>

      <Tabs defaultValue="balance-sheet">
        <TabsList>
          <TabsTrigger value="balance-sheet" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><FileSpreadsheet className="h-4 w-4" /> {t('pages.financialReports.balanceSheet')}</TabsTrigger>
          <TabsTrigger value="profit-loss" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><BarChart3 className="h-4 w-4" /> {t('pages.financialReports.profitLoss')}</TabsTrigger>
        </TabsList>

        <TabsContent value="balance-sheet" className="mt-6">
          <Card>
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle>{t('pages.financialReports.balanceSheet')}</CardTitle>
                  <p className="text-sm text-muted-foreground">{t('pages.financialReports.asOf')} {format(new Date(), 'MMMM d, yyyy')}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <AccountSection title={t('pages.financialReports.assets')} accounts={balanceSheet?.assets} color="text-blue-600" />
                  <div className={`flex justify-between font-bold border-t pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span>{t('pages.financialReports.totalAssets')}</span>
                    <span className="font-mono">${formatCurrency(balanceSheet?.totals?.assets)}</span>
                  </div>
                </div>

                <div className="space-y-6">
                  <AccountSection title={t('pages.financialReports.liabilities')} accounts={balanceSheet?.liabilities} color="text-red-600" />
                  <div className={`flex justify-between font-semibold border-t pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span>{t('pages.financialReports.totalLiabilities')}</span>
                    <span className="font-mono">${formatCurrency(balanceSheet?.totals?.liabilities)}</span>
                  </div>

                  <AccountSection title={t('pages.financialReports.equity')} accounts={balanceSheet?.equity} color="text-purple-600" />
                  <div className={`flex justify-between font-semibold border-t pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span>{t('pages.financialReports.totalEquity')}</span>
                    <span className="font-mono">${formatCurrency(balanceSheet?.totals?.equity)}</span>
                  </div>

                  <div className={`flex justify-between font-bold text-lg border-t-2 pt-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span>{t('pages.financialReports.totalLiabilitiesEquity')}</span>
                    <span className="font-mono">${formatCurrency(balanceSheet?.totals?.liabilities_and_equity)}</span>
                  </div>
                </div>
              </div>

              {balanceSheet?.totals && Math.abs(balanceSheet.totals.assets - balanceSheet.totals.liabilities_and_equity) > 0.01 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  <strong>{t('pages.financialReports.warning')}:</strong> {t('pages.financialReports.notBalanced')}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profit-loss" className="mt-6">
          <Card>
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <CardTitle>{t('pages.financialReports.profitLossStatement')}</CardTitle>
                  <p className="text-sm text-muted-foreground">{t('pages.financialReports.forPeriod')} {format(new Date(), 'MMMM d, yyyy')}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <AccountSection title={t('pages.financialReports.revenue')} accounts={profitLoss?.revenue} color="text-emerald-600" />
              <div className={`flex justify-between font-semibold border-t pt-2 text-emerald-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('pages.financialReports.totalRevenue')}</span>
                <span className="font-mono">${formatCurrency(profitLoss?.totals?.revenue)}</span>
              </div>

              <AccountSection title={t('pages.financialReports.expenses')} accounts={profitLoss?.expenses} color="text-amber-600" />
              <div className={`flex justify-between font-semibold border-t pt-2 text-amber-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('pages.financialReports.totalExpenses')}</span>
                <span className="font-mono">${formatCurrency(profitLoss?.totals?.expenses)}</span>
              </div>

              <div className={`flex justify-between font-bold text-xl border-t-2 pt-4 ${(profitLoss?.totals?.net_income || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'} ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {(profitLoss?.totals?.net_income || 0) >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                  {t('pages.financialReports.netIncome')}
                </span>
                <span className="font-mono">${formatCurrency(profitLoss?.totals?.net_income)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialReports;
